import { Action, Selector, State, StateContext } from "@ngxs/store";
import { LoadingState } from "@tsin-core/entities";
import { inject, Injectable } from "@angular/core";
import { TrackService } from "@tsin-core/services/http/track.service";
import { tap, catchError } from "rxjs";
import { IQuestion, QuestionStateModel } from "./question.model";
import { CreateQuestion, DeleteQuestion, Question, Questions, SetQuestionSearch, UpdateQuestion } from "./question.action";
import { QuestionService } from "@tsin-core/services/http/question.service";

@State<QuestionStateModel>({
    name: 'questionState',
    defaults: {
        searchQuery: '',
        questions: [],
        question: null,
        loading: LoadingState.notLoading
    }
})

@Injectable()

export class QuestionState {

    private questionService = inject(QuestionService)

    @Selector()
    static getQuestions(state: QuestionStateModel) {
        return state.questions;
    }

    @Selector()
    static getLoading(state: QuestionStateModel) {
        return state.loading;
    }

    @Selector()
    static filteredItems(state: QuestionStateModel) {
        // console.log('searchQuery: ', state.searchQuery)
        const query = state.searchQuery;
        if (!query || query === undefined) {
            return state.questions;  // Return all questions when searchQuery is empty
        }
        return state.questions.filter((question: any) =>
            question?.question?.toLowerCase().includes(query.toLowerCase())  // Include partial matches
        ); 
    }

    @Action(Questions)
    getQuestions({patchState}: StateContext<QuestionStateModel>) {
        patchState({ loading: LoadingState.loadingList });
        return this.questionService.getQuestions().pipe(
            tap((questions: IQuestion[] | any) => {
                patchState({ questions: questions.data, loading: LoadingState.notLoading });
            }),
            catchError((error) => {
                patchState({ loading: LoadingState.notLoading });
                throw error;
            })
        );
    }

    @Action(Question)
    getQuestion(ctx: StateContext<QuestionStateModel>, action: Question) {
        return this.questionService.getQuestion(action.payload).pipe(
            tap((question: IQuestion) => {
                const state = ctx.getState();
                ctx.setState({
                ...state,
                question: question,
                });
            }),
            catchError((error) => {
                ctx.patchState({ loading: LoadingState.notLoading });
                throw error
            })
        );
    }

    @Action(CreateQuestion)
    createQuestion({patchState, getState}: StateContext<QuestionStateModel>, action: CreateQuestion) {
        patchState({ loading: LoadingState.loadingAddUpdate });
        return this.questionService.createQuestion(action.payload).pipe(
            tap((result: IQuestion) => {
                const state = getState();
                patchState({
                    questions: [...state.questions, result as IQuestion],
                    loading: LoadingState.notLoading
                });
            }),
            catchError((error) => {
                patchState({ loading: LoadingState.notLoading });
                throw error;
            })
        );
    }

    @Action(UpdateQuestion)
    updateQuestion({patchState, getState}: StateContext<QuestionStateModel>, action: UpdateQuestion) {
        patchState({ loading: LoadingState.loadingAddUpdate });
        return this.questionService.updateQuestion(action.payload, action.id).pipe(
            tap((result) => {
                let questions = getState().questions;
                const index = questions.findIndex((question) => question.id === result.id);
                questions[index] = result;
                patchState({
                    questions,
                    loading: LoadingState.notLoading
                });
            }),
            catchError((error) => {
                patchState({ loading: LoadingState.notLoading });
                throw error
            })
        );
    }

    @Action(DeleteQuestion)
    deleteQuestion({patchState, getState}: StateContext<QuestionStateModel>, action: DeleteQuestion) {
        patchState({ loading: LoadingState.loadingDelete });
        return this.questionService.deleteQuestion(action.payload).pipe(
            tap(() => {
                let questions = getState().questions;
                questions = questions.filter(
                    (question) => question.id !== action.payload
                );
                patchState({
                    questions,
                    loading: LoadingState.notLoading
                });
            }),
            catchError((error) => {
                patchState({ loading: LoadingState.notLoading });
                throw error
            })
        );
    }

    // Action to set the search query
    @Action(SetQuestionSearch)
    setSearchQuery(ctx: StateContext<QuestionStateModel>, action: SetQuestionSearch) {
        ctx.patchState({ loading: LoadingState.loadingList });
        setTimeout(() => {
            ctx.patchState({
                searchQuery: action.query,
                loading: LoadingState.notLoading
            });
        }, 1000)
    }

}